import React, { useMemo } from 'react';
import { Link } from 'react-router';
import { toast } from 'react-toastify';

import { Button, buttonVariants } from '@/components/ui/button';
import { TiptapRichTextEditor } from 'components/DesignSystems/New/TiptapRichTextEditor';
import { useRefreshSitrepV2 } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';

import ChildAorMapButton from './ChildAorMapButton';
import { useAorText } from 'utils/aorTerminology';

type ChildAorReportAutomation = {
  provider?: string;
  childAorReport?: { aorId?: string; aorName?: string; path?: string[] };
  [key: string]: unknown;
};

type SitrepSectionLike = {
  id: string;
  name?: string;
  type?: string;
  automation?: ChildAorReportAutomation;
  output_format_structured?: {
    automation?: ChildAorReportAutomation;
    [key: string]: unknown;
  };
  section_versions?: Array<{
    data_string?: string | null;
    sitrep_section_text?: string | null;
    status?: string | null;
    timestamp?: string | null;
  }>;
};

export type ChildAorReportPayload = {
  aorId: string;
  aorName: string;
  path?: string[];
  sitrepId: string | null;
  sitrepName: string | null;
  templateId?: string | null;
  sameTemplate?: boolean;
  publishedAt: string | null;
  sections?: Array<{ name: string; html: string }>;
  notes?: string[];
  error?: string;
};

export function getChildAorReportAutomation(
  section: SitrepSectionLike | null | undefined
): ChildAorReportAutomation | null {
  const automation =
    section?.automation ?? section?.output_format_structured?.automation;
  return automation?.provider === 'childAorReport' ? automation : null;
}

export function isAutomatedChildAorReportSection(
  section: SitrepSectionLike | null | undefined
): boolean {
  return (
    section?.type === 'automated' &&
    getChildAorReportAutomation(section) !== null
  );
}

export function parseChildAorReportPayload(
  dataString: string | null | undefined
): ChildAorReportPayload | null {
  if (!dataString) return null;
  try {
    const parsed = JSON.parse(dataString) as ChildAorReportPayload;
    return parsed && typeof parsed === 'object' && 'aorId' in parsed
      ? parsed
      : null;
  } catch {
    return null;
  }
}

/**
 * Hub Reports tab, filtered to the child AOR with the report expanded (same
 * shape as review notification links).
 */
export const childReportHref = (sitrepId: string, aorId?: string | null) => {
  const params = new URLSearchParams({
    openTab: 'Reports',
    sitrepTab: 'CurrentSITREP',
    openReport: sitrepId,
  });
  if (aorId) params.set('aorId', aorId);
  return `/hub?${params.toString()}`;
};

type ChildAorReportSectionPanelProps = {
  sitrepId: string;
  section: SitrepSectionLike;
  selectedDatetime?: string;
  disabled?: boolean;
};

export default function ChildAorReportSectionPanel({
  sitrepId,
  section,
  selectedDatetime,
  disabled = false,
}: ChildAorReportSectionPanelProps) {
  const aorText = useAorText();
  const refreshSitrepMutation = useRefreshSitrepV2();
  const version = section.section_versions?.[0] || null;
  const payload = useMemo(
    () => parseChildAorReportPayload(version?.data_string),
    [version?.data_string]
  );
  const automation = getChildAorReportAutomation(section);
  const aorId = payload?.aorId || automation?.childAorReport?.aorId || null;
  const aorName =
    payload?.aorName ||
    automation?.childAorReport?.aorName ||
    section.name ||
    aorText('Subordinate AOR');

  const isProcessing = useMemo(() => {
    if (!version) return false;
    const status = (version.status || '').toLowerCase();
    return (
      !!status &&
      status !== 'completed' &&
      status !== 'summarized' &&
      status !== 'error'
    );
  }, [version]);

  const isBusy = disabled || isProcessing || refreshSitrepMutation.isPending;

  const handleRefresh = () => {
    refreshSitrepMutation.mutate(
      {
        id: sitrepId,
        selectedDatetime:
          selectedDatetime || version?.timestamp || new Date().toISOString(),
        sections: [section.id],
      },
      {
        onError: (error) => {
          toast.error(`Failed to refresh the report for ${aorName}.`);
          console.error('Error refreshing child AOR report section:', error);
        },
      }
    );
  };

  const html = version?.sitrep_section_text || '';

  return (
    <div
      className="ChildAorReportSectionPanel tw:rounded-lg tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground"
      data-testid="child-aor-report-section"
    >
      <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
        <div>
          <strong>{aorName}</strong>
          <small className="tw:mt-0.5 tw:block tw:text-muted-foreground">
            {payload?.sitrepId
              ? `Snapshot of the latest published report for ${aorName}.`
              : `Includes the latest published report for ${aorName} when one exists.`}
          </small>
        </div>
        <div className="tw:flex tw:items-center tw:gap-2">
          <ChildAorMapButton aorId={aorId} aorName={aorName} />
          {payload?.sitrepId ? (
            <Link
              className={buttonVariants({ variant: 'outline', size: 'sm' })}
              to={childReportHref(payload.sitrepId, aorId)}
            >
              Open report
            </Link>
          ) : null}
          <Button
            type="button"
            size="sm"
            disabled={isBusy}
            onClick={handleRefresh}
          >
            Refresh
          </Button>
        </div>
      </div>
      {isProcessing ? (
        <ReportsGeneratingPlaceholder />
      ) : html ? (
        <TiptapRichTextEditor
          value={html}
          readOnly
          showToolbar={false}
          className="reports-section__html-content"
          normalizeMarkdown
        />
      ) : (
        <em className="tw:text-muted-foreground">
          No published report for {aorName} yet.
        </em>
      )}
    </div>
  );
}
